import { memo } from 'react'
import {
  BasicResponsiveImage,
  Li,
  Link,
  StandardTile,
  StandardTileContents,
  StandardTileDescription,
  StandardTileImage,
  StandardTileName,
  StandardTileOverlay,
} from '@vp/swan'
import type { NavigationItem } from '../../../../types/navigation'
import { getHeaderTracker } from '../../../../tracking'
import { CATEGORY_SECTION, MERCHANDISING_SECTION, PROMO_TILE, TILE_LIST_TILE, TOP_NAV } from '../../../../constants/analytics'
import { Badge } from '../../../common/Badge'
import { ComponentType } from '../../../../types/components-map'

interface PromotionalTileProps {
  readonly navItem: NavigationItem
  readonly parentText: string
  readonly isVisualCategory: boolean
}

/**
 * Promotional visual tile within the flyout (e.g., seasonal promotions).
 */
export const PromotionalTile: React.FC<PromotionalTileProps> = memo(({ navItem, parentText, isVisualCategory }) => {
  const handleTracking = () => {
    getHeaderTracker().trackLink({
      subSection: TOP_NAV,
      navigationDetailPath: [parentText, navItem.analyticsId || navItem.text],
      navigationSection: isVisualCategory ? undefined : navItem.inMx ? MERCHANDISING_SECTION : CATEGORY_SECTION,
      href: navItem.url,
      linkType: isVisualCategory ? TILE_LIST_TILE : PROMO_TILE,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (['Enter', ' '].includes(e.key)) {
      handleTracking()
    }
  }

  return (
    <Li>
      <StandardTile
        layout="vertical"
        imageWidth="full-bleed"
        render={(props) => (
          <Link
            href={navItem.url}
            className={props.className}
            onClick={handleTracking}
            onKeyDown={handleKeyDown}
            data-position={navItem.itemPosition}
            data-translation={[parentText, navItem.analyticsId].join('|')}
            data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
          >
            {props.children}
          </Link>
        )}
      >
        {navItem.badge && (
          <StandardTileOverlay>
            <Badge badge={navItem.badge} />
          </StandardTileOverlay>
        )}
        <StandardTileImage>
          <BasicResponsiveImage aspectRatio={1} alt={navItem.text} src={navItem?.image?.url} loading="lazy" decoding="async" />
        </StandardTileImage>
        <StandardTileContents>
          <StandardTileName>{navItem.text}</StandardTileName>
          <StandardTileDescription>{navItem.description}</StandardTileDescription>
        </StandardTileContents>
      </StandardTile>
    </Li>
  )
})
